import { useId, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import type { PortfolioProject } from '@/lib/portfolio'

/**
 * Problem / Solution / Result — redesigned per the Stitch portfolio
 * reference (`stitch_ea_studio_cosmic_web_design/portfolio`): three
 * independently-collapsible rows stacked in a single column (each its own
 * disclosure, not one button revealing a 3-col grid), Result open by
 * default and shown as a bullet list. Same real BS/EN copy as before —
 * only the presentation changed. Bullets are the same sentences from
 * `caseStudy.result(En)` split on '. ', not new invented content.
 */
export function CaseStudyAccordion({ project }: { project: PortfolioProject }) {
  const { t, lang } = useLanguage()
  const cs = project.caseStudy
  const [openRow, setOpenRow] = useState<'problem' | 'solution' | 'result' | null>('result')

  const resultBullets = (lang === 'en' ? cs.resultEn : cs.result)
    .split('. ')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.endsWith('.') ? s : `${s}.`))

  return (
    <div className="flex flex-col gap-1 border-t border-white/10 pt-4">
      <AccordionRow
        label={t('pf-problem')}
        open={openRow === 'problem'}
        onToggle={() => setOpenRow((v) => (v === 'problem' ? null : 'problem'))}
      >
        <p className="text-sm leading-relaxed text-foreground-muted">{lang === 'en' ? cs.problemEn : cs.problem}</p>
      </AccordionRow>

      <AccordionRow
        label={t('pf-solution')}
        open={openRow === 'solution'}
        onToggle={() => setOpenRow((v) => (v === 'solution' ? null : 'solution'))}
      >
        <p className="text-sm leading-relaxed text-foreground-muted">{lang === 'en' ? cs.solutionEn : cs.solution}</p>
      </AccordionRow>

      <AccordionRow
        label={t('pf-result')}
        open={openRow === 'result'}
        onToggle={() => setOpenRow((v) => (v === 'result' ? null : 'result'))}
      >
        <ul className="flex flex-col gap-1.5">
          {resultBullets.map((bullet) => (
            <li key={bullet} className="flex gap-2 text-sm leading-relaxed text-foreground-muted">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent-amber-light" aria-hidden="true" />
              {bullet}
            </li>
          ))}
        </ul>
      </AccordionRow>

      <div className="mt-3 flex flex-wrap gap-2">
        {cs.stack.map((item) => (
          <span
            key={item}
            className="rounded-pill border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-foreground-subtle"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

function AccordionRow({
  label,
  open,
  onToggle,
  children,
}: {
  label: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  const panelId = useId()
  return (
    <div className="border-b border-white/[0.06] last:border-b-0">
      <h4>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-4 py-3 text-left text-sm font-semibold text-foreground transition-colors hover:text-accent-fire"
        >
          {label}
          <ChevronDown
            size={16}
            className={`shrink-0 text-foreground-muted transition-transform duration-normal ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </h4>
      {open && (
        <div id={panelId} className="pb-4">
          {children}
        </div>
      )}
    </div>
  )
}
